import { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import {
  useCreateSepaSetupIntent,
  useConfirmSepaSetup,
  useSepaStatus,
} from '@/hooks/useBilling';
import type { SepaStatusResponse } from '@/services/billing.service';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle, CreditCard, Landmark, Clock, Info } from 'lucide-react';
import { useOrganisationsStore } from '@/store/organisations.store';

/**
 * Enregistrement du moyen de paiement.
 *
 * [2026-09] Décision Rose du 20/09/2026 : les clients enregistrent une CARTE
 * bancaire (le SetupIntent côté API ne propose plus que la carte). Les mandats
 * SEPA déjà signés restent valides et s'affichent tels quels. Les composants
 * gardent leur nom historique (« Sepa… ») pour ne pas toucher à leurs usages.
 */

interface PaymentMethodFormProps {
  clientSecret: string;
  setupIntentId: string;
}

const PaymentMethodForm = ({
  clientSecret,
  setupIntentId,
}: PaymentMethodFormProps) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const confirmBackend = useConfirmSepaSetup();
  const { refetch } = useSepaStatus();
  const [enCours, setEnCours] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret || enCours) return;
    setEnCours(true);

    try {
      // Submit the Elements form to validate before confirming
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message);
        return;
      }

      // URL de retour : ne sert que si la banque exige une authentification
      // (3-D Secure) et que Stripe quitte la page. AppLayout rattache alors la
      // carte au compte à partir de `setup_intent` dans l'URL.
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('sepa_setup', 'success');

      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnUrl.toString(),
        },
        // Sans authentification bancaire, on reste sur la page : le
        // rattachement se fait tout de suite, sans attendre le webhook.
        redirect: 'if_required',
      });

      if (result.error) {
        toast.error(result.error.message);
        return;
      }

      try {
        await confirmBackend.mutateAsync(setupIntentId);
        toast.success(t('billing.sepa.success.title', 'Carte enregistrée'));
      } catch {
        toast.error(
          t(
            'billing.sepa.confirmFailed',
            'La carte est enregistrée chez Stripe mais son rattachement à votre compte a échoué. Rechargez la page ; si le problème persiste, écrivez-nous.'
          )
        );
        return;
      }
      await refetch();
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <Button
        type="submit"
        disabled={!stripe || !elements || enCours}
        className="w-full"
      >
        {t('billing.sepa.confirm', 'Enregistrer la carte')}
      </Button>
    </form>
  );
};

/** Ce qui est enregistré : une carte (marque, 4 derniers chiffres, échéance) ou un mandat SEPA. */
const MoyenDePaiementEnregistre = ({ data }: { data: SepaStatusResponse }) => {
  const { t } = useTranslation();
  // Sans `paymentMethodType` (ancienne API), on ne présume pas d'une carte.
  const carte = data.paymentMethodType === 'card';
  const marque = data.brand
    ? data.brand.charAt(0).toUpperCase() + data.brand.slice(1)
    : null;
  const Icone = carte ? CreditCard : Landmark;
  const details = carte
    ? [
        marque,
        data.lastFour ? `•••• ${data.lastFour}` : null,
        data.expiry
          ? t('billing.sepa.expires', 'expire {{date}}', { date: data.expiry })
          : null,
      ]
    : [data.bankName, data.lastFour ? `•••• ${data.lastFour}` : null];
  const ligne = details.filter(Boolean).join(' · ');

  return (
    <div className="border-success-200 bg-success-50 rounded-xl border p-4">
      <div className="flex items-start gap-3">
        <div className="bg-success-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
          <CheckCircle className="text-success-600 h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-success-900 text-sm font-semibold">
            {carte
              ? t('billing.sepa.active', 'Carte enregistrée')
              : t('billing.sepa.activeSepa', 'Mandat de prélèvement actif')}
          </p>
          {ligne && (
            <div className="text-success-700 mt-1 flex items-center gap-2 text-xs">
              <Icone className="h-3.5 w-3.5" />
              <span>{ligne}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SepaMandateSetup = ({
  hideGracePeriodBanner = false,
}: {
  hideGracePeriodBanner?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { data, isFetched: sepaStatusFetched } = useSepaStatus();
  const orgUuid = useOrganisationsStore(s => s.currentOrganisation?.uuid);
  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [preparationEchouee, setPreparationEchouee] = useState(false);
  const [lastOrgUuid, setLastOrgUuid] = useState<string | undefined>(orgUuid);
  const {
    mutateAsync: createSetupIntent,
    isPending: isCreateSetupIntentPending,
  } = useCreateSepaSetupIntent();

  // Reset state when organisation changes
  if (orgUuid !== lastOrgUuid) {
    setLastOrgUuid(orgUuid);
    setClientSecret(null);
    setSetupIntentId(null);
    setPreparationEchouee(false);
  }

  // Get current locale for Stripe (map i18n language to Stripe locale)
  const getStripeLocale = useCallback((): 'fr' | 'en' | 'auto' => {
    const lang = i18n.language;
    // Stripe supports: auto, en, fr, de, es, it, ja, nl, pt, sv, etc.
    if (lang.startsWith('fr')) return 'fr';
    if (lang.startsWith('en')) return 'en';
    return 'auto';
  }, [i18n.language]);

  useEffect(() => {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as
      | string
      | undefined;
    if (key) {
      // Load Stripe with locale
      setStripePromise(loadStripe(key, { locale: getStripeLocale() }));
    }
  }, [getStripeLocale]);

  useEffect(() => {
    if (
      sepaStatusFetched &&
      !data?.hasValidMandate &&
      data?.canManage !== false &&
      !clientSecret &&
      !isCreateSetupIntentPending &&
      !preparationEchouee
    ) {
      (async () => {
        try {
          const res = await createSetupIntent();
          setClientSecret(res.clientSecret);
          if (res.publishableKey) {
            setStripePromise(
              loadStripe(res.publishableKey, { locale: getStripeLocale() })
            );
          }
          setSetupIntentId(res.setupIntentId);
        } catch {
          // Pas d'attente infinie sur « Préparation… » (verdict QA 20/09/2026).
          setPreparationEchouee(true);
        }
      })();
    }
  }, [
    sepaStatusFetched,
    data?.hasValidMandate,
    data?.canManage,
    clientSecret,
    isCreateSetupIntentPending,
    preparationEchouee,
    orgUuid,
    createSetupIntent,
    getStripeLocale,
  ]);

  // Compte dispensé (interne, test) : rien à enregistrer, rien à montrer.
  if (data?.exempt) {
    return null;
  }

  // Un moyen déjà enregistré s'affiche sans Stripe : la clé publique n'arrive
  // qu'avec la création du formulaire, jamais faite dans ce cas (défaut vu sur
  // le staging le 20/09/2026 : « clé Stripe non configurée » à la place de la
  // carte).
  if (data?.hasValidMandate) {
    return <MoyenDePaiementEnregistre data={data} />;
  }

  // Simple membre du cabinet : il ne peut pas poser la carte, on le dit au
  // lieu de lui présenter un formulaire refusé.
  if (data?.canManage === false) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
        <Info className="text-primary-600 mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>
          {t(
            'billing.sepa.ownerOnly',
            'La carte bancaire du cabinet est enregistrée par son propriétaire, depuis la page Facturation.'
          )}
        </span>
      </div>
    );
  }

  const carteExpiree =
    data?.paymentMethodType === 'card' && data.expiry ? data : null;

  if (preparationEchouee) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
        {t(
          'billing.sepa.prepareFailed',
          "Le formulaire de paiement n'a pas pu être préparé. Rechargez la page ; si le problème persiste, écrivez-nous."
        )}
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-neutral-600">
        {t(
          'billing.sepa.missingKey',
          'Stripe publishable key is not configured'
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {carteExpiree && (
        <p className="text-warning-800 text-sm">
          {t(
            'billing.sepa.expired',
            'Votre carte •••• {{lastFour}} a expiré ({{expiry}}). Enregistrez une nouvelle carte.',
            {
              lastFour: carteExpiree.lastFour ?? '',
              expiry: carteExpiree.expiry ?? '',
            }
          )}
        </p>
      )}
      {data?.isInGracePeriod && !hideGracePeriodBanner && (
        <div className="border-warning-200 bg-warning-50 rounded-xl border p-4">
          <div className="flex items-start gap-3">
            <div className="bg-warning-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
              <Clock className="text-warning-600 h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-warning-900 text-sm font-semibold">
                {t('billing.sepa.gracePeriod.title', 'Période de grâce active')}
              </p>
              <p className="text-warning-700 mt-1 text-xs leading-relaxed">
                {t(
                  'billing.sepa.gracePeriod.description',
                  "Vous pouvez utiliser les fonctionnalités sans carte enregistrée jusqu'au {{date}}. Après cette date, une carte bancaire sera nécessaire pour continuer.",
                  {
                    date: data?.gracePeriodEnd
                      ? new Date(data.gracePeriodEnd).toLocaleDateString(
                          'fr-FR',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )
                      : 'N/A',
                  }
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      {clientSecret ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            locale: getStripeLocale(),
          }}
        >
          <PaymentMethodForm
            clientSecret={clientSecret}
            setupIntentId={setupIntentId || ''}
          />
        </Elements>
      ) : (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-neutral-600">
          {t(
            'billing.sepa.loading',
            'Préparation du formulaire de paiement...'
          )}
        </div>
      )}
      <p className="text-muted text-xs">
        {t(
          'billing.sepa.terms',
          'En enregistrant votre carte, vous autorisez le règlement automatique de vos factures mensuelles.'
        )}{' '}
        <a
          href="https://stripe.com/terms"
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 underline"
        >
          {t('billing.sepa.link', 'Conditions de Stripe')}
        </a>
        .
      </p>
    </div>
  );
};
